/**
 * Proposal page — orchestrates the full manual-approval workflow.
 *
 * Refactored in Sub-batch E3.3.b:
 *  - Server state is fetched via TanStack Query hooks (see /hooks/useProposal.js).
 *  - Presentation is split into /components/proposal/*.
 *  - Mutations invalidate only the affected data (see /lib/queryInvalidate.js).
 *
 * This file is intentionally a thin orchestration container — no fetch logic
 * lives here beyond wiring hooks to child props.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";

import api from "@/lib/api";
import { PROP } from "@/constants/testIds";
import { useConfirm } from "@/components/ConfirmDialog";
import PrepareTradeDialog from "@/components/PrepareTradeDialog";
import TakeSkipTrade from "@/components/TakeSkipTrade";

import ProposalHeader from "@/components/proposal/ProposalHeader";
import VersionComparison from "@/components/proposal/VersionComparison";
import ProposalChart from "@/components/proposal/ProposalChart";
import AnalystPanel from "@/components/proposal/AnalystPanel";
import TradePlanComparison from "@/components/proposal/TradePlanComparison";
import PlanEditor from "@/components/proposal/PlanEditor";
import RiskChecksPanel from "@/components/proposal/RiskChecksPanel";

import {
  useProposal, useProposalVersions, usePortfolios,
  useBars, useSizing, useProposalMutations,
} from "@/hooks/useProposal";

const DEFAULT_TF_BY_HOLDING = { intraday: "15m", swing: "1D", position: "1D" };
const PORTFOLIO_KEY_BY_HOLDING = { position: "LONG_TERM", intraday: "TACTICAL", swing: "SWING" };

export default function Proposal() {
  const { id } = useParams();
  const nav = useNavigate();
  const confirm = useConfirm();

  // Server state
  const { data: prop, isLoading: loadingProp } = useProposal(id);
  const { data: versions = [] } = useProposalVersions(id);
  const { data: portfolios = [] } = usePortfolios();
  const mutations = useProposalMutations(id, prop?.symbol);

  // Local UI state
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [entry, setEntry] = useState("");
  const [stop, setStop]   = useState("");
  const [target, setTarget] = useState("");
  const [qty, setQty] = useState("");
  const [portfolioKey, setPortfolioKey] = useState("SWING");
  const [verdict, setVerdict] = useState(null);
  const [busy, setBusy] = useState(false);
  const [timeframe, setTimeframe] = useState("1D");
  const [showMAs, setShowMAs] = useState(true);
  const [showCompare, setShowCompare] = useState(false);
  const [preparing, setPreparing] = useState(false);

  // Derived
  const selectedPlan = useMemo(
    () => (prop?.analyst?.plans || []).find((p) => p.id === selectedPlanId),
    [prop, selectedPlanId]
  );

  // Initialise selectedPlan + timeframe on load
  useEffect(() => {
    if (!prop) return;
    const rec = (prop.consensus || {}).recommended_plan_id || "B";
    setSelectedPlanId(rec);
    const plan = (prop.analyst?.plans || []).find((p) => p.id === rec);
    if (plan) setTimeframe(DEFAULT_TF_BY_HOLDING[plan.holding_period] || "1D");
  }, [prop?.id, prop]);

  // Sync inputs to selected plan
  useEffect(() => {
    if (!selectedPlan) return;
    setEntry(String(selectedPlan.entry));
    setStop(String(selectedPlan.stop));
    setTarget(String(selectedPlan.targets?.[0]?.price ?? ""));
    setQty("");
    setVerdict(null);
    setPortfolioKey(PORTFOLIO_KEY_BY_HOLDING[selectedPlan.holding_period] || "SWING");
  }, [selectedPlan?.id, selectedPlan]);

  // Bars — TanStack Query with MEDIUM staleTime
  const { data: bars = [], isLoading: barsLoading } = useBars(prop?.symbol, timeframe);

  // Sizing — auto-recompute on entry/stop/portfolio/symbol change
  const { data: sizing } = useSizing({
    symbol: prop?.symbol,
    entry, stop, portfolioKey, proposalId: id,
  });

  // When sizing arrives and qty is empty, seed qty from approved_qty.
  useEffect(() => {
    if (sizing && (!qty || String(qty) === "0")) {
      setQty(String(sizing.approved_qty));
    }
  }, [sizing?.approved_qty, sizing, qty]);

  // ---- Handlers ----
  const doRerun = async () => {
    const ok = await confirm.confirm({
      variant: "warning",
      title: "Rerun the AI debate?",
      description: "This will create a new proposal version with fresh market data. Full history is preserved and the current version becomes 'superseded'.",
      details: { "AI budget cost": "~1× analyst + 1× critic call", cooldown: "30 seconds after last debate" },
      confirmLabel: "Rerun Debate",
      testId: "rerun-debate-confirm",
    });
    if (!ok) return;
    try {
      const data = await mutations.rerun.mutateAsync();
      toast.success(`New version v${data.version} created.`);
      nav(`/proposal/${data.id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Rerun failed");
    }
  };

  const runRiskCheck = async () => {
    if (!selectedPlanId) { toast.error("Select a plan first."); return; }
    setBusy(true);
    try {
      const data = await mutations.runRiskCheck.mutateAsync({
        plan_id: selectedPlanId,
        entry: parseFloat(entry), stop: parseFloat(stop),
        target: parseFloat(target), qty: parseInt(qty, 10),
      });
      setVerdict(data.verdict);
      toast[data.verdict.pass ? "success" : "error"](data.verdict.pass ? "Risk Engine PASS" : "Risk Engine BLOCK");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Risk check failed");
    } finally { setBusy(false); }
  };

  const applyFix = async (action, value) => {
    setBusy(true);
    try {
      const data = await mutations.applyRiskFix.mutateAsync({ planId: selectedPlanId, action, value });
      setEntry(String(data.params.entry));
      setStop(String(data.params.stop));
      setTarget(String(data.params.target));
      setQty(String(data.params.qty));
      setVerdict(data.verdict);
      toast[data.verdict.pass ? "success" : "error"](
        data.verdict.pass ? "Fix applied — Risk Engine PASS" : "Fix applied — some checks still failing"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fix failed");
    } finally { setBusy(false); }
  };

  const reject = async () => {
    const reason = await confirm.prompt({
      variant: "warning",
      title: `Reject ${prop.symbol}?`,
      description: "This trade will be dismissed and moved to the audit log. Please note why so you can review it later.",
      placeholder: "e.g. thesis weakened, better setup elsewhere, market context shifted…",
      confirmLabel: "Reject Proposal",
      cancelLabel: "Keep Open",
      testId: "reject-proposal-prompt",
    });
    if (!reason) return;
    await mutations.reject.mutateAsync(reason);
    toast.info("Proposal rejected.");
    nav("/dashboard");
  };

  const approve = () => {
    if (!verdict?.pass) { toast.error("Run Risk Check with a passing verdict first."); return; }
    setPreparing(true);
  };

  const onPrepareApproved = (data) => {
    toast.success(`Order submitted · ${data.order.status.toUpperCase()} · Protection ${data.protection_status.toUpperCase()}`);
    nav("/positions");
  };

  const onEditForm = (field, value) => {
    if (field === "entry")   setEntry(value);
    if (field === "stop")    setStop(value);
    if (field === "target")  setTarget(value);
    if (field === "qty")     setQty(value);
    setVerdict(null);
  };

  const applySuggestion = (field, value) => {
    onEditForm(field, String(value));
    toast.success(`${field[0].toUpperCase() + field.slice(1)} updated to ${value}`);
  };

  const applyAllSuggestions = (next) => {
    if (next.entry  !== undefined) setEntry(String(next.entry));
    if (next.stop   !== undefined) setStop(String(next.stop));
    if (next.target !== undefined) setTarget(String(next.target));
    if (next.qty    !== undefined) setQty(String(next.qty));
    setVerdict(null);
    toast.success("Applied all critic suggestions to the manual approval form");
  };

  if (loadingProp || !prop) return (
    <div className="p-8 text-muted-foreground text-sm" data-testid="proposal-loading">
      <Loader2 className="inline animate-spin mr-2" size={14}/>Loading proposal…
    </div>
  );

  const a = prop.analyst || {};
  const c = prop.critic || {};
  const q = prop.market_snapshot || {};
  const plans = a.plans || [];
  const reviewMap = Object.fromEntries((c.plan_reviews || []).map((r) => [r.plan_id, r]));

  const overlays = {
    priceLines: [
      q.price && { price: q.price, color: "#a1a1aa", style: "dotted", title: "MKT" },
      selectedPlan?.entry && { price: selectedPlan.entry, color: "#0A84FF", style: "solid", title: `ENTRY · ${selectedPlan.label}` },
      selectedPlan?.stop && { price: selectedPlan.stop, color: "#FF3B30", style: "dashed", title: "STOP" },
      ...(selectedPlan?.targets || []).map((t, i) => ({ price: t.price, color: "#34C759", style: "dashed", title: `T${i + 1} · ${t.sell_pct}%` })),
      ...(a.support || []).slice(0, 2).map((s) => ({ price: s, color: "#22C55E33", style: "dotted", title: "SUP" })),
      ...(a.resistance || []).slice(0, 2).map((r) => ({ price: r, color: "#EAB30833", style: "dotted", title: "RES" })),
      a.invalidation_level && { price: a.invalidation_level, color: "#FF3B3080", style: "large_dashed", title: "INVAL" },
    ].filter(Boolean),
    movingAverages: showMAs ? [
      { period: 20, color: "#3B82F6", enabled: true },
      { period: 50, color: "#F59E0B", enabled: true },
    ] : [],
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid={PROP.root}>
      <ProposalHeader
        prop={prop}
        versions={versions}
        onBack={() => nav(-1)}
        onToggleCompare={() => setShowCompare((v) => !v)}
        onRerun={doRerun}
        rerunning={mutations.rerun.isPending}
      />

      {prop.status !== "executed" && prop.status !== "closed" && (
        <TakeSkipTrade
          proposalId={id}
          decision={prop.user_decision}
          onDecided={() => { /* proposal will re-fetch via query invalidation from mutations */ }}
          onOpenPrepare={() => {
            if (!selectedPlanId) {
              toast.error("Select a plan first (A / B / C in the plans table below).");
              return;
            }
            if (!verdict?.pass) {
              toast.error("Run Risk Check with a passing verdict before placing the trade.");
              return;
            }
            setPreparing(true);
          }}
        />
      )}

      {showCompare && (
        <VersionComparison
          versions={versions}
          currentId={prop.id}
          onClose={() => setShowCompare(false)}
        />
      )}

      {(a._source === "fallback" || c._source === "fallback") && (
        <div className="grid-cell border-signal-yellow/50 bg-signal-yellow/5 flex items-center gap-3" role="alert">
          <ShieldAlert className="text-signal-yellow shrink-0" size={16} aria-hidden="true"/>
          <div className="text-xs">
            AI unavailable — using deterministic fallback for{" "}
            {[a._source === "fallback" && "Analyst", c._source === "fallback" && "Critic"].filter(Boolean).join(" & ")}. Verify risk verdict before approving.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-border/60">
        <ProposalChart
          bars={bars}
          timeframe={timeframe}
          onTimeframeChange={setTimeframe}
          overlays={overlays}
          loading={barsLoading}
          showMAs={showMAs}
          onToggleMAs={setShowMAs}
        />
        <AnalystPanel analyst={a}/>
      </div>

      <TradePlanComparison
        plans={plans}
        reviewMap={reviewMap}
        selectedPlanId={selectedPlanId}
        selectedPlan={selectedPlan}
        recommendedId={(prop.consensus || {}).recommended_plan_id}
        criticConcerns={c.portfolio_concerns}
        currentEntry={entry}
        currentStop={stop}
        currentTarget={target}
        currentQty={qty}
        critic={c}
        onSelectPlan={setSelectedPlanId}
        onApplyField={applySuggestion}
        onApplyAll={applyAllSuggestions}
        proposalId={id}
        portfolioKey={portfolioKey}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border/60">
        <PlanEditor
          selectedPlanId={selectedPlanId}
          entry={entry} stop={stop} target={target} qty={qty}
          onChange={onEditForm}
          sizing={sizing}
          portfolios={portfolios}
          portfolioKey={portfolioKey}
          onPortfolio={setPortfolioKey}
          busy={busy}
          verdict={verdict}
          onRiskCheck={runRiskCheck}
          onApprove={approve}
          onReject={reject}
        />
        <RiskChecksPanel
          verdict={verdict}
          applyingFix={mutations.applyRiskFix.isPending}
          onApplyFix={applyFix}
        />
      </div>

      <PrepareTradeDialog
        open={preparing}
        onClose={() => setPreparing(false)}
        proposalId={id}
        approveParams={{
          plan_id: selectedPlanId,
          entry: parseFloat(entry), stop: parseFloat(stop),
          target: parseFloat(target), qty: parseInt(qty, 10),
          symbol: prop.symbol,
        }}
        defaultPortfolioKey={PORTFOLIO_KEY_BY_HOLDING[selectedPlan?.holding_period] || "SWING"}
        onApproved={onPrepareApproved}
      />
    </div>
  );
}
